import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip, Skeleton, Button } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import AppCard from '../common/AppCard';
import AiLimitBanner from '../common/AiLimitBanner';
import api from '../../services/api';

// Shared tone → icon/color mapping for AI insight lists (also used by the
// category analysis dialog). Tone icons signal status: good / watch / info.
export const TONE_META = {
  good: { Icon: CheckCircleRoundedIcon, color: 'success.main' },
  watch: { Icon: WarningAmberRoundedIcon, color: 'warning.main' },
  info: { Icon: InfoRoundedIcon, color: 'info.main' },
};

// Display order for insights, matching SUMMARY_SECTIONS in
// backend/routes/ai.js. Insights are rendered as one flat list (no section
// headers), but keeping backend section order groups related items together
// as the list flows down the columns.
const SECTION_ORDER = ['cash-flow', 'spending', 'budgets', 'upcoming', 'housekeeping'];

const sortInsights = (insights) => {
  const rank = (i) => {
    const idx = SECTION_ORDER.indexOf(i.section);
    // Unrecognized sections (older cached summaries) keep their position at
    // the end rather than interleaving with known ones.
    return idx === -1 ? SECTION_ORDER.length : idx;
  };
  return [...insights].sort((a, b) => rank(a) - rank(b));
};

const InsightRow = ({ insight }) => {
  const { Icon, color } = TONE_META[insight.tone] || TONE_META.info;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, breakInside: 'avoid' }}>
      <Icon sx={{ fontSize: '0.9rem', color, mt: '2px', flexShrink: 0 }} />
      <Typography variant="body3" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
        {insight.text}
      </Typography>
    </Box>
  );
};

/*
  AiSummaryCard: short Claude-written narrative of the Overview aggregates.
  Props:
   - range: the Overview time-range key ('current-month', 'week', ...)
   - data: pre-aggregated figures to summarize (already memoized by the parent)

  The backend caches per user + range on a hash of `data`, so re-fetching on
  every range flip is cheap — only genuinely new numbers cost an API call.
  When AI isn't configured on the backend (503) the card removes itself.
*/
const AiSummaryCard = ({ range, data }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  // Generations left in the user's global daily AI allowance (shared across
  // the whole app); null until the backend has told us. Drives the
  // Regenerate button state.
  const [remaining, setRemaining] = useState(null);
  // True when the backend served the last cached summary because the daily
  // generation allowance ran out, so the numbers may be behind the page.
  const [stale, setStale] = useState(false);
  // Ignore responses from superseded requests (rapid range flipping).
  const requestIdRef = useRef(0);

  // Serialized once so the fetch effect keys off content, not object identity.
  const dataJson = useMemo(() => JSON.stringify(data), [data]);

  const insights = useMemo(() => sortInsights(summary?.insights || []), [summary]);

  const fetchSummary = useCallback(async (force) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.post('/ai/summary', {
        range,
        data: JSON.parse(dataJson),
        force,
      });
      if (requestId !== requestIdRef.current) return;
      setSummary(res.summary);
      if (typeof res.aiCredits?.remaining === 'number') setRemaining(res.aiCredits.remaining);
      setStale(Boolean(res.stale));
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      if (err.response?.status === 503) {
        setUnavailable(true);
      } else {
        if (typeof err.response?.data?.aiCredits?.remaining === 'number') {
          setRemaining(err.response.data.aiCredits.remaining);
        }
        setError(err.response?.data?.error || 'Could not generate a summary right now.');
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [range, dataJson]);

  useEffect(() => {
    if (unavailable) return undefined;
    // Debounce so range flipping and initial data churn coalesce to one call.
    const timer = setTimeout(() => fetchSummary(false), 600);
    return () => clearTimeout(timer);
  }, [fetchSummary, unavailable]);

  if (unavailable) return null;

  return (
    <AppCard
      // Colored to match the "AI Insights" affordance on Accounts/Categories cards
      title={<Box component="span" sx={{ color: 'secondary.main' }}>AI Insights</Box>}
      icon={<AutoAwesomeIcon sx={{ color: 'secondary.main' }} />}
      // The card must size to its content (AppCard defaults to height: 100%)
      // and never shrink. Sections lay out in a multi-column grid on wider
      // screens, so the full summary stays visible without capping the height
      // or scrolling internally.
      sx={{
        height: 'auto',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
      action={
        <Tooltip
          title={
            remaining === 0
              ? 'Daily AI limit reached. Available again tomorrow'
              : 'Regenerate'
          }
        >
          {/* span keeps the tooltip working while the button is disabled */}
          <span>
            <IconButton
              size="small"
              onClick={() => fetchSummary(true)}
              disabled={loading || remaining === 0}
              aria-label="Regenerate AI insights"
              sx={{ color: 'text.secondary' }}
            >
              <RefreshIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </span>
        </Tooltip>
      }
      contentSx={{ px: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}
    >
      {loading && !summary ? (
        <Box>
          <Skeleton width="55%" height={22} />
          <Skeleton width="90%" height={18} />
          <Skeleton width="75%" height={18} />
        </Box>
      ) : error && !summary ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {error}
          </Typography>
          <Button size="small" onClick={() => fetchSummary(false)}>
            Try again
          </Button>
        </Box>
      ) : summary ? (
        // Dim (rather than blank) while a refresh is in flight
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 150ms ease' }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {summary.headline}
          </Typography>
          {/* One flat list flowed through CSS columns so the browser balances
              items by height (minimizing the card's height), rather than one
              section per column. Column count is capped at the insight count
              so a short summary doesn't leave empty columns. Rows are spaced
              with bottom margins (flex gap has no effect inside a column
              container) and set breakInside: 'avoid' so an insight never
              splits across two columns. */}
          <Box
            sx={{
              columnCount: {
                xs: 1,
                sm: Math.min(2, insights.length || 1),
                lg: Math.min(3, insights.length || 1),
              },
              columnGap: 3,
              '& > *': { mb: 0.75, '&:last-child': { mb: 0 } },
            }}
          >
            {insights.map((insight, index) => (
              <InsightRow key={index} insight={insight} />
            ))}
          </Box>
          {stale && (
            <AiLimitBanner sx={{ mt: 1 }}>
              Daily AI limit reached. Showing the last generated summary.
            </AiLimitBanner>
          )}
          {error && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      ) : null}
    </AppCard>
  );
};

export default AiSummaryCard;
